// src/contexts/AdminAuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';

// Create admin context
const AdminAuthContext = createContext();

// Admin accounts from environment variables
const ADMIN_ACCOUNTS = [
  {
    id: 1,
    username: process.env.REACT_APP_ADMIN_USERNAME,
    password: process.env.REACT_APP_ADMIN_PASSWORD,
    role: 'super_admin',
    name: 'Super Admin'
  }
];

// Admin session duration: 3 hours
const ADMIN_SESSION_DURATION = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
const ADMIN_SALT = 'ekah_health_admin_secure_salt_2024'; // Salt for key derivation

// AES-GCM Encryption utilities for Admin
const AdminCrypto = {
  // Derive a cryptographic key from password using PBKDF2 with salt
  async deriveKey(password) {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    const salt = encoder.encode(ADMIN_SALT);

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000, // High iterations for security
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  },

  // Encrypt sensitive data using AES-GCM
  async encrypt(data, password) {
    try {
      const key = await this.deriveKey(password);
      const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
      const encoder = new TextEncoder();

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encoder.encode(data)
      );

      // Combine IV + encrypted data and encode as base64
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('AES-GCM encryption failed:', error);
      throw error;
    }
  },

  // Decrypt sensitive data using AES-GCM
  async decrypt(encryptedData, password) {
    try {
      const key = await this.deriveKey(password);
      const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

      // Extract IV (first 12 bytes) and encrypted data
      const iv = combined.slice(0, 12);
      const data = combined.slice(12);

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        data
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('AES-GCM decryption failed:', error);
      return null;
    }
  }
};

// Generate a secure session key
const generateSessionKey = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Check if admin session has expired
const isAdminSessionExpired = () => {
  const expiryTime = sessionStorage.getItem('adminSessionExpiry');
  if (!expiryTime) return true;
  return Date.now() > parseInt(expiryTime, 10);
};

// Admin auth provider component
export const AdminAuthProvider = ({ children }) => {
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing admin session in sessionStorage
    const restoreSession = async () => {
      try {
        const encryptedSession = sessionStorage.getItem('adminSession');
        const sessionKey = sessionStorage.getItem('adminSessionKey');

        if (encryptedSession && sessionKey) {
          // Check if session has expired
          if (isAdminSessionExpired()) {
            sessionStorage.removeItem('adminSession');
            sessionStorage.removeItem('adminSessionKey');
            sessionStorage.removeItem('adminSessionExpiry');
            setLoading(false);
            return;
          }

          // Decrypt the session data
          const encryptionKey = `ekah_admin_${sessionKey}`;
          const decryptedSession = await AdminCrypto.decrypt(encryptedSession, encryptionKey);

          if (decryptedSession) {
            const adminData = JSON.parse(decryptedSession);
            setCurrentAdmin(adminData);
          } else {
            // Decryption failed, clear session
            sessionStorage.removeItem('adminSession');
            sessionStorage.removeItem('adminSessionKey');
            sessionStorage.removeItem('adminSessionExpiry');
          }
        }
      } catch (err) {
        console.error('Session restoration failed:', err);
        sessionStorage.removeItem('adminSession');
        sessionStorage.removeItem('adminSessionKey');
        sessionStorage.removeItem('adminSessionExpiry');
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  // Admin login
  const adminLogin = async (username, password) => {
    setError(null);
    try {
      const admin = ADMIN_ACCOUNTS.find(
        acc => acc.username === username && acc.password === password
      );

      if (!admin) {
        throw new Error('Invalid admin credentials');
      }

      const adminSession = {
        id: admin.id,
        username: admin.username,
        role: admin.role,
        name: admin.name,
        loginTime: new Date().toISOString()
      };

      // Generate session key for encryption
      const sessionKey = generateSessionKey();
      const encryptionKey = `ekah_admin_${sessionKey}`;

      // Encrypt the session data
      const encryptedSession = await AdminCrypto.encrypt(JSON.stringify(adminSession), encryptionKey);

      // Calculate expiry time (3 hours from now)
      const expiryTime = Date.now() + ADMIN_SESSION_DURATION;

      setCurrentAdmin(adminSession);
      sessionStorage.setItem('adminSession', encryptedSession);
      sessionStorage.setItem('adminSessionKey', sessionKey);
      sessionStorage.setItem('adminSessionExpiry', expiryTime.toString());

      return adminSession;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Admin logout
  const adminLogout = async () => {
    setError(null);
    try {
      setCurrentAdmin(null);
      sessionStorage.removeItem('adminSession');
      sessionStorage.removeItem('adminSessionKey');
      sessionStorage.removeItem('adminSessionExpiry');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Check if admin has specific permission
  const hasPermission = (permission) => {
    if (!currentAdmin) return false;

    // Super admin has all permissions
    if (currentAdmin.role === 'super_admin') return true;

    // Manager has limited permissions
    if (currentAdmin.role === 'manager') {
      const managerPermissions = ['view_services', 'edit_services', 'view_users'];
      return managerPermissions.includes(permission);
    }

    return false;
  };

  const value = {
    currentAdmin,
    loading,
    error,
    adminLogin,
    adminLogout,
    clearError,
    hasPermission,
    isAuthenticated: !!currentAdmin
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Custom hook to use admin auth context
export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};